import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PluginScope } from '@orbind/core'
import { api } from '../../lib/api.ts'

/**
 * Plugins (SPEC-20260926-2036) — duas superfícies com donos diferentes.
 *
 * `/console/plugins` é do super admin: cadastrar o que pode existir nesta
 * instalação. `/plugins` é do administrador do workspace: instalar o que já
 * está cadastrado. Chaves de cache separadas, porque cadastrar não muda o que
 * está instalado e vice-versa.
 */

export type PluginExtensaoResumo = { type: string; key: string; config: unknown }

export type PluginCadastrado = {
  id: string
  key: string
  version: string
  name: Record<string, string>
  baseUrl: string
  vendor: { name?: string; url?: string }
  scopes: PluginScope[]
  manifestUrl: string
  arquivado: boolean
  extensoes: PluginExtensaoResumo[]
}

export type PluginInstalado = {
  id: string
  status: string
  acceptedScopes: PluginScope[]
  installedAt: string
  pluginId: string
  key: string
  name: Record<string, string>
  version: string
  vendor: { name?: string; url?: string }
}

/**
 * As colunas `jsonb` (`name`, `vendor`, `config`) chegam do RPC como
 * `JSONValue` — o tipo não sabe o que o servidor guardou lá, e não tem como
 * saber. O cast na fronteira é onde a forma volta a ser declarada; o resto do
 * arquivo trabalha com tipo de verdade.
 */
const comoJson = <T,>(p: Promise<unknown>): Promise<T> => p as Promise<T>

const CHAVE_CONSOLE = ['console', 'plugins']
const CHAVE_WORKSPACE = ['plugins']

/** Nome na locale da pessoa, com queda para qualquer uma que exista. */
export function nomeDoPlugin(name: Record<string, string>, locale = 'pt-BR'): string {
  return name[locale] ?? Object.values(name)[0] ?? '(sem nome)'
}

/* ------------------------------------------------------------- console --- */

export function usePluginsDoConsole() {
  return useQuery({
    queryKey: CHAVE_CONSOLE,
    queryFn: async () => comoJson<{ plugins: PluginCadastrado[] }>((await api.console.plugins.$get()).json()),
  })
}

export function useCadastrarPlugin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (manifestUrl: string) =>
      (await api.console.plugins.$post({ json: { manifestUrl } })).json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHAVE_CONSOLE })
      // cadastrar muda o catálogo que o workspace enxerga
      void queryClient.invalidateQueries({ queryKey: CHAVE_WORKSPACE })
    },
  })
}

/** Rebusca o manifesto e atualiza versão e extensões. */
export function useAtualizarPlugin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.console.plugins[':id'].refresh.$post({ param: { id } })).json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHAVE_CONSOLE })
      void queryClient.invalidateQueries({ queryKey: CHAVE_WORKSPACE })
    },
  })
}

/* ----------------------------------------------------------- workspace --- */

export function usePluginsDoWorkspace() {
  return useQuery({
    queryKey: CHAVE_WORKSPACE,
    queryFn: async () =>
      comoJson<{
        disponiveis: PluginCadastrado[]
        instalados: PluginInstalado[]
        escopos: readonly PluginScope[]
      }>((await api.plugins.$get()).json()),
  })
}

export function useInstalarPlugin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entrada: { id: string; scopes: readonly string[] }) =>
      (
        await api.plugins[':id'].install.$post({
          param: { id: entrada.id },
          json: { scopes: [...entrada.scopes] },
        })
      ).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_WORKSPACE }),
  })
}

export function useDesinstalarPlugin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (installationId: string) =>
      (await api.plugins.installations[':id'].$delete({ param: { id: installationId } })).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_WORKSPACE }),
  })
}
